// src/admin/components/JsonViewer.jsx
import React, { useState, useMemo } from 'react';
import { Copy, Check, Search, ChevronRight, ChevronDown, X } from 'lucide-react';

// Light-themed, searchable JSON tree. Replaces the raw <pre> dump (JsonBlock)
// for the superadmin Run Detail intermediate-output sections so they are
// readable and filterable in-place.
const COLORS = {
  key:      '#7c3aed',
  string:   '#15803d',
  number:   '#1d4ed8',
  boolean:  '#b91c1c',
  null:     '#9ca3af',
  index:    '#6c6c6d',
  punct:    '#9ca3af',
  muted:    '#6c6c6d',
  border:   'rgba(0,0,0,0.08)',
  surface:  'rgba(0,0,0,0.02)',
};
const MONO = "'JetBrains Mono', monospace";
const SANS = "'DM Sans', sans-serif";
const INDENT = 16;

function safeStringify(v) {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function leafMatches(v, q) {
  if (v === null) return 'null'.includes(q);
  const t = typeof v;
  if (t === 'string' || t === 'number' || t === 'boolean') {
    return String(v).toLowerCase().includes(q);
  }
  return false;
}

// True when this node — or any descendant — matches the (already lowercased) query.
function subtreeMatches(matchKey, value, q) {
  if (matchKey != null && String(matchKey).toLowerCase().includes(q)) return true;
  if (value !== null && typeof value === 'object') {
    if (Array.isArray(value)) return value.some(c => subtreeMatches(null, c, q));
    return Object.entries(value).some(([k, c]) => subtreeMatches(k, c, q));
  }
  return leafMatches(value, q);
}

function countMatches(matchKey, value, q) {
  let n = 0;
  if (matchKey != null && String(matchKey).toLowerCase().includes(q)) n += 1;
  if (value !== null && typeof value === 'object') {
    const entries = Array.isArray(value) ? value.map(c => [null, c]) : Object.entries(value);
    for (const [k, c] of entries) n += countMatches(k, c, q);
  } else if (leafMatches(value, q)) {
    n += 1;
  }
  return n;
}

function Highlight({ text, q }) {
  const s = String(text);
  if (!q) return <>{s}</>;
  const lower = s.toLowerCase();
  const out = [];
  let i = 0;
  while (i < s.length) {
    const idx = lower.indexOf(q, i);
    if (idx === -1) { out.push(s.slice(i)); break; }
    if (idx > i) out.push(s.slice(i, idx));
    out.push(
      <mark key={idx} style={{ background: '#fde68a', color: 'inherit', borderRadius: '2px', padding: '0 1px' }}>
        {s.slice(idx, idx + q.length)}
      </mark>
    );
    i = idx + q.length;
  }
  return <>{out}</>;
}

function PrimitiveValue({ value, q }) {
  if (value === null) return <span style={{ color: COLORS.null }}>null</span>;
  const t = typeof value;
  if (t === 'boolean') return <span style={{ color: COLORS.boolean }}>{String(value)}</span>;
  if (t === 'number')  return <span style={{ color: COLORS.number }}>{String(value)}</span>;

  const s = String(value);
  const asBlock = s.length > 80 || s.includes('\n');
  if (asBlock) {
    return (
      <div style={{
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        color: '#1d1d1f', background: COLORS.surface,
        border: `1px solid ${COLORS.border}`, borderRadius: '6px',
        padding: '8px 10px', marginTop: '4px',
        fontFamily: SANS, fontSize: '12px', lineHeight: 1.55,
      }}>
        <Highlight text={s} q={q} />
      </div>
    );
  }
  return (
    <span style={{ color: COLORS.string }}>
      "<Highlight text={s} q={q} />"
    </span>
  );
}

function Label({ label, kind, q }) {
  if (label == null) return null;
  if (kind === 'index') {
    return <span style={{ color: COLORS.index }}>[{label}]</span>;
  }
  return (
    <span style={{ color: COLORS.key, fontWeight: 600 }}>
      <Highlight text={label} q={kind === 'key' ? q : ''} />
    </span>
  );
}

function Node({ label, kind, value, depth, query, expandAll }) {
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const matchKey = kind === 'key' ? label : null;

  const isContainer = value !== null && typeof value === 'object';
  const initialOpen = expandAll === true ? true
    : expandAll === false ? false
    : depth < 2;
  const [open, setOpen] = useState(initialOpen);

  if (searching && !subtreeMatches(matchKey, value, q)) return null;

  const rowStyle = {
    paddingLeft: `${depth * INDENT}px`,
    paddingTop: '1px',
    fontFamily: MONO,
    fontSize: '12px',
    lineHeight: 1.6,
    color: '#1d1d1f',
  };

  if (!isContainer) {
    return (
      <div style={rowStyle}>
        <Label label={label} kind={kind} q={searching ? q : ''} />
        {label != null && <span style={{ color: COLORS.punct }}>: </span>}
        <PrimitiveValue value={value} q={searching ? q : ''} />
      </div>
    );
  }

  const isArr = Array.isArray(value);
  const entries = isArr
    ? value.map((c, i) => ({ k: i, kind: 'index', v: c }))
    : Object.entries(value).map(([k, v]) => ({ k, kind: 'key', v }));
  const count = entries.length;
  const summary = isArr
    ? `${count} ${count === 1 ? 'elemento' : 'elementi'}`
    : `${count} ${count === 1 ? 'campo' : 'campi'}`;
  const effectiveOpen = searching ? true : open;

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => { if (!searching) setOpen(o => !o); }}
        onKeyDown={e => { if (!searching && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(o => !o); } }}
        style={{
          ...rowStyle,
          display: 'flex', alignItems: 'center', gap: '3px',
          cursor: searching ? 'default' : 'pointer', userSelect: 'none',
        }}
      >
        {effectiveOpen
          ? <ChevronDown size={13} color={COLORS.muted} style={{ flexShrink: 0 }} />
          : <ChevronRight size={13} color={COLORS.muted} style={{ flexShrink: 0 }} />}
        <Label label={label} kind={kind} q={searching ? q : ''} />
        {label != null && <span style={{ color: COLORS.punct }}>: </span>}
        <span style={{ color: COLORS.punct }}>{isArr ? '[' : '{'}</span>
        {!effectiveOpen && (
          <>
            <span style={{ color: COLORS.muted, fontStyle: 'italic', fontSize: '11px', margin: '0 4px' }}>{summary}</span>
            <span style={{ color: COLORS.punct }}>{isArr ? ']' : '}'}</span>
          </>
        )}
      </div>
      {effectiveOpen && (
        <>
          {entries.map(({ k, kind: ck, v }) => (
            <Node key={k} label={k} kind={ck} value={v} depth={depth + 1} query={query} expandAll={expandAll} />
          ))}
          <div style={{ ...rowStyle, color: COLORS.punct }}>{isArr ? ']' : '}'}</div>
        </>
      )}
    </div>
  );
}

const toolBtn = {
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  padding: '4px 8px', borderRadius: '6px',
  border: '1px solid rgba(0,0,0,0.1)', background: '#fff',
  color: '#3a3a3c', fontSize: '11px', cursor: 'pointer',
  fontFamily: SANS,
};

export default function JsonViewer({ value, maxHeight = 420 }) {
  const [query, setQuery] = useState('');
  const [expandAll, setExpandAll] = useState(null);
  const [treeKey, setTreeKey] = useState(0);
  const [copied, setCopied] = useState(false);

  const q = query.trim().toLowerCase();
  const matchCount = useMemo(() => (q ? countMatches(null, value, q) : 0), [value, q]);

  function setExpansion(v) {
    setExpandAll(v);
    setTreeKey(k => k + 1);
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(safeStringify(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  }

  if (value == null) {
    return (
      <div style={{ padding: '16px', color: COLORS.muted, fontStyle: 'italic', fontSize: '13px', fontFamily: SANS }}>
        Non disponibile
      </div>
    );
  }

  const isContainer = typeof value === 'object';
  const rootEntries = !isContainer ? null
    : Array.isArray(value)
      ? value.map((c, i) => ({ k: i, kind: 'index', v: c }))
      : Object.entries(value).map(([k, v]) => ({ k, kind: 'key', v }));

  return (
    <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
        padding: '8px 10px', borderBottom: `1px solid ${COLORS.border}`,
        background: COLORS.surface,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px', flex: '1 1 200px',
          background: '#fff', border: `1px solid ${COLORS.border}`,
          borderRadius: '6px', padding: '5px 8px',
        }}>
          <Search size={13} color={COLORS.muted} style={{ flexShrink: 0 }} />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Cerca in questa sezione..."
            style={{
              border: 'none', outline: 'none', background: 'transparent',
              fontSize: '12px', fontFamily: SANS, color: '#1d1d1f',
              flex: 1, minWidth: 0,
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              title="Cancella ricerca"
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex', color: COLORS.muted }}
            >
              <X size={13} />
            </button>
          )}
        </div>
        {q && (
          <span style={{ fontSize: '11px', color: COLORS.muted, fontFamily: SANS, whiteSpace: 'nowrap' }}>
            {matchCount} {matchCount === 1 ? 'risultato' : 'risultati'}
          </span>
        )}
        <button type="button" onClick={() => setExpansion(true)}  style={toolBtn}>Espandi tutto</button>
        <button type="button" onClick={() => setExpansion(false)} style={toolBtn}>Comprimi tutto</button>
        <button
          type="button"
          onClick={handleCopy}
          style={{ ...toolBtn, color: copied ? '#10B981' : '#3a3a3c' }}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? 'Copiato' : 'Copia'}
        </button>
      </div>

      {/* Tree */}
      <div style={{ maxHeight, overflow: 'auto', padding: '10px 12px' }}>
        {q && matchCount === 0 ? (
          <div style={{ color: COLORS.muted, fontStyle: 'italic', fontSize: '12px', fontFamily: SANS, padding: '4px 0' }}>
            Nessun risultato per "{query.trim()}"
          </div>
        ) : isContainer ? (
          <div key={treeKey}>
            {rootEntries.map(({ k, kind, v }) => (
              <Node key={k} label={k} kind={kind} value={v} depth={0} query={query} expandAll={expandAll} />
            ))}
          </div>
        ) : (
          <Node key={treeKey} label={null} kind={null} value={value} depth={0} query={query} expandAll={expandAll} />
        )}
      </div>
    </div>
  );
}
